<script lang="ts">
  // Audio Pack — player-supplied background audio, kept SEPARATE from the visual Texture Pack (this is
  // the "different" thing the owner wanted split out). Clips live locally in IndexedDB (audio store)
  // and follow the screen: overworld queue in the world, duel queue in a duel, a losing-track variant.
  // NO default audio by design. Rendered inline in Settings.
  import { session } from "../stores/session.svelte.ts";
  import { settings, setAudioVolume } from "../stores/settings.svelte.ts";
  import { addAudio, deleteAudio, listAudio, applyAudioVolume, type AudioScene, type AudioItem } from "../stores/audio.svelte.ts";

  const isWorld = $derived(session.mode === "world");

  let duelAudio = $state<AudioItem[]>([]);
  let losingAudio = $state<AudioItem[]>([]);
  let overworldAudio = $state<AudioItem[]>([]);
  async function refresh(): Promise<void> {
    duelAudio = await listAudio("duel");
    losingAudio = await listAudio("duel-losing");
    overworldAudio = await listAudio("overworld");
  }
  refresh();
  async function onUpload(scene: AudioScene, e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement;
    for (const file of Array.from(input.files ?? [])) await addAudio(scene, file);
    input.value = "";
    await refresh();
  }
  async function onDelete(id: number, scene: AudioScene): Promise<void> {
    await deleteAudio(id, scene);
    await refresh();
  }
</script>

{#snippet audioList(title: string, scene: AudioScene, items: AudioItem[], hint?: string)}
  <div class="chamfer bg-void-2/40 p-3" style="--c:9px">
    <div class="mb-1.5 flex items-center justify-between gap-2">
      <span class="min-w-0">
        <span class="block font-display text-sm font-semibold text-ink">{title}</span>
        {#if hint}<span class="block text-xs text-ink-dim">{hint}</span>{/if}
      </span>
      <label class="shrink-0 cursor-pointer chamfer bg-cyan/15 px-2.5 py-1 font-display text-[0.56rem] font-semibold uppercase tracking-[0.16em] text-cyan transition-colors hover:bg-cyan/25" style="--c:5px">
        + Add
        <input type="file" accept="audio/*" multiple class="hidden" onchange={(e) => onUpload(scene, e)} />
      </label>
    </div>
    {#if items.length === 0}
      <p class="text-xs text-ink-dim">Nothing added — silent by default.</p>
    {:else}
      <ul class="space-y-1">
        {#each items as a (a.id)}
          <li class="flex items-center justify-between gap-2 rounded bg-void/50 px-2 py-1">
            <span class="min-w-0 truncate text-xs text-ink-dim">{a.name}</span>
            <button onclick={() => onDelete(a.id, scene)} class="shrink-0 text-ink-dim transition-colors hover:text-magenta" aria-label="Remove {a.name}">
              <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/snippet}

<div class="space-y-2">
  <div class="chamfer bg-void-2/40 px-4 py-3" style="--c:9px">
    <label class="block">
      <span class="mb-1.5 flex items-center justify-between">
        <span class="font-display text-sm font-semibold text-ink">Audio volume</span>
        <span class="text-xs tabular-nums text-ink-dim">{Math.round(settings.audioVolume * 100)}%</span>
      </span>
      <input type="range" min="0" max="1" step="0.05" value={settings.audioVolume} class="w-full accent-cyan" oninput={(e) => { setAudioVolume(+e.currentTarget.value); applyAudioVolume(); }} />
    </label>
  </div>
  {#if isWorld}{@render audioList("Overworld", "overworld", overworldAudio)}{/if}
  {@render audioList("Duel", "duel", duelAudio)}
  {@render audioList("Duel — losing", "duel-losing", losingAudio, "Plays while you're behind on life points.")}
</div>
